// Emotional Tone Filter (S018) - FIXED
// Color-coded multi-select filter for emotional tones
import React from 'react';

export type EmotionalTone = 'urgency' | 'hope' | 'fear' | 'joy' | 'neutral';

interface ToneConfig {
  name: EmotionalTone;
  emoji: string;
  color: string;
  bgColor: string;
  description: string;
}

const TONE_CONFIG: ToneConfig[] = [
  { 
    name: 'urgency', 
    emoji: '🔴', 
    color: 'text-red-600',
    bgColor: 'bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50',
    description: 'Time-sensitive, act now'
  },
  { 
    name: 'hope', 
    emoji: '💚', 
    color: 'text-green-600',
    bgColor: 'bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50',
    description: 'Positive change, brighter future'
  },
  { 
    name: 'fear', 
    emoji: '🟠', 
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50',
    description: 'Loss, danger, consequences'
  },
  { 
    name: 'joy', 
    emoji: '💛', 
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50',
    description: 'Celebration, happiness'
  },
  { 
    name: 'neutral', 
    emoji: '⚪', 
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700/30 dark:hover:bg-gray-700/50',
    description: 'Informational, factual'
  }
];

interface Props {
  selected: EmotionalTone[];
  onChange: (tones: EmotionalTone[]) => void;
  counts?: Record<EmotionalTone, number>;
  showCounts?: boolean;
  showDescriptions?: boolean;
  compact?: boolean;
}

export const EmotionalToneFilter: React.FC<Props> = ({ 
  selected, 
  onChange, 
  counts,
  showCounts = false,
  showDescriptions = false,
  compact = false
}) => {
  const toggleTone = (tone: EmotionalTone) => {
    if (selected.includes(tone)) {
      onChange(selected.filter(t => t !== tone));
    } else {
      onChange([...selected, tone]);
    }
  };

  const selectAll = () => {
    onChange(TONE_CONFIG.map(t => t.name));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="bg-white dark:bg-boxdark rounded-lg p-4 border border-stroke dark:border-strokedark">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-black dark:text-white">Emotional Tone</h3>
        <div className="flex gap-2 text-xs">
          <button 
            onClick={selectAll}
            className="text-primary hover:underline"
          >
            Select All
          </button>
          <span className="text-gray-400">|</span>
          <button 
            onClick={clearAll}
            className="text-gray-500 hover:underline"
          >
            Clear
          </button>
        </div>
      </div>
      
      <div className={`flex flex-wrap gap-2 ${compact ? '' : 'flex-col'}`}>
        {TONE_CONFIG.map(tone => {
          const isSelected = selected.includes(tone.name);
          const count = counts?.[tone.name];
          
          return (
            <button
              key={tone.name}
              onClick={() => toggleTone(tone.name)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                ${isSelected 
                  ? `${tone.bgColor} ring-2 ring-primary ring-offset-1` 
                  : 'bg-gray-50 dark:bg-meta-4 hover:bg-gray-100 dark:hover:bg-meta-4/80'
                }
                ${compact ? '' : 'w-full justify-between'}
              `}
              title={tone.description}
            >
              <div className="flex items-center gap-2">
                <span>{tone.emoji}</span>
                <span className={`font-medium ${isSelected ? tone.color : 'text-gray-700 dark:text-gray-300'}`}>
                  {tone.name.charAt(0).toUpperCase() + tone.name.slice(1)}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {showDescriptions && !compact && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {tone.description}
                  </span>
                )}
                {showCounts && count !== undefined && (
                  <span className={`
                    text-xs px-2 py-0.5 rounded-full
                    ${isSelected 
                      ? 'bg-white/50 dark:bg-black/20' 
                      : 'bg-gray-200 dark:bg-meta-4'
                    }
                  `}>
                    {count}
                  </span>
                )}
                {isSelected && (
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>
      
      {selected.length > 0 && (
        <div className="mt-3 pt-3 border-t border-stroke dark:border-strokedark">
          <div className="text-xs text-gray-500">
            Showing creatives with: {selected.map(t => (
              <span key={t} className="inline-flex items-center gap-1 mx-1">
                {TONE_CONFIG.find(c => c.name === t)?.emoji} {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { TONE_CONFIG };
export default EmotionalToneFilter;
